import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  InventorySlideOver,
  slideOverCancelClass,
  slideOverPrimaryClass,
} from '../../ui/InventorySlideOver';
import { Callout } from '../../ui/Callout';
import {
  extendDemoData,
  fetchDemoExtendPreview,
  ALL_SECTIONS,
  DemoExtendPreview,
  DemoExtendResult,
  DemoExtendSectionPreview,
} from '../../../services/automation/automationService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { formatPlainDate } from '../../../utils/dateUtils';
import {
  borderPrimary,
  darkerBG,
  mutedText,
  primaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';

interface DemoExtendModalProps {
  isOpen: boolean;
  restaurantId: string;
  onClose: () => void;
}

/** One "where this stream ends / how far behind it is" line. */
const GapRow: React.FC<{ label: string; detail: string; amount: string }> = ({ label, detail, amount }) => (
  <div className={cn('flex items-center justify-between gap-3 rounded-lg px-3 py-2', darkerBG)}>
    <div className="min-w-0">
      <p className={cn(primaryText, smallTextSize, semiBoldText)}>{label}</p>
      <p className={cn(mutedText, 'text-xs')}>{detail}</p>
    </div>
    <span className={cn(primaryText, smallTextSize, semiBoldText, 'shrink-0 text-right')}>{amount}</span>
  </div>
);

/**
 * "Fill to today" — extends an existing template-generated demo account up to
 * yesterday without re-uploading a menu template. The gap is detected server-side
 * per stream; this drawer only confirms it and fires the generation.
 *
 * Food and drinks live in separate sections, so the default target is ALL of them:
 * filling an account to today normally means all of it, and a section with nothing
 * to fill is skipped server-side rather than failing the run.
 */
const DemoExtendModal: React.FC<DemoExtendModalProps> = ({ isOpen, restaurantId, onClose }) => {
  const { t } = useTranslation();
  const restaurantSections = useRestaurantStore(s => s.restaurantSections);
  const sections = useMemo(() => restaurantSections[restaurantId] || [], [restaurantSections, restaurantId]);

  const [sectionId, setSectionId] = useState('');
  const [preview, setPreview] = useState<DemoExtendPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<DemoExtendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The drawer stays mounted while closed (that is what animates the slide-in), so
  // every effect below is gated on isOpen. Clearing on OPEN rather than on close is
  // deliberate: closing mid-generation still lets the in-flight request resolve into
  // state, so only a reset here guarantees a reopen never shows the last run's numbers.
  useEffect(() => {
    if (!isOpen) return;
    setSectionId('');
    setPreview(null);
    setResult(null);
    setError(null);
  }, [isOpen]);

  // Sections arrive with the restaurant store load, so pick the target as soon as they
  // are there rather than making the user open the dropdown: the only section when there
  // is one, all of them otherwise.
  useEffect(() => {
    if (!isOpen || sectionId || sections.length === 0) return;
    setSectionId(sections.length === 1 ? sections[0].id : ALL_SECTIONS);
  }, [isOpen, sectionId, sections]);

  const loadPreview = useCallback(async () => {
    if (!isOpen || !sectionId) return;
    setIsLoadingPreview(true);
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      setPreview(await fetchDemoExtendPreview(restaurantId, sectionId));
    } catch (err: any) {
      setError(err.message || t('profile.demoExtend.previewFailed'));
    } finally {
      setIsLoadingPreview(false);
    }
  }, [isOpen, restaurantId, sectionId, t]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      setResult(await extendDemoData(restaurantId, sectionId));
    } catch (err: any) {
      setError(err.message || t('profile.demoExtend.generateFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  const streamDetail = (lastDate: string | null) =>
    lastDate ? t('profile.demoExtend.lastEntry', { date: formatPlainDate(lastDate) }) : t('profile.demoExtend.noEntries');

  /** A section's two stream rows — or, when the run will skip it, the one line saying why. */
  const renderSectionGap = (section: DemoExtendSectionPreview) => {
    if (!section.canExtend) {
      return (
        <p className={cn(mutedText, 'text-xs px-3 py-2 rounded-lg', darkerBG)}>
          {section.reason === 'UP_TO_DATE'
            ? t('profile.demoExtend.sectionUpToDate')
            : t('profile.demoExtend.sectionNoCostPriceAnalyses')}
        </p>
      );
    }

    return (
      <>
        <GapRow
          label={t('profile.demoExtend.revenue')}
          detail={streamDetail(section.revenue.lastDate)}
          amount={t('profile.demoExtend.daysToGenerate', { count: section.revenue.daysToGenerate })}
        />
        <GapRow
          label={t('profile.demoExtend.orders')}
          detail={streamDetail(section.orders.lastDate)}
          amount={t('profile.demoExtend.daysToGenerate', { count: section.orders.daysToGenerate })}
        />
      </>
    );
  };

  const canGenerate = !!preview?.canExtend && !isGenerating && !isLoadingPreview && !result;

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('profile.demoExtend.title')}
      description={t('profile.demoExtend.description')}
      closeLabel={t('common.close')}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={isGenerating} className={slideOverCancelClass}>
            {result ? t('common.close') : t('common.cancel')}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={cn(slideOverPrimaryClass, 'flex items-center justify-center gap-2')}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('profile.demoExtend.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t('profile.demoExtend.generate')}
                </>
              )}
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {sections.length === 0 ? (
          <Callout tone="warning" text={t('profile.demoExtend.noSections')} />
        ) : (
          <div>
            <label htmlFor="demo-extend-section" className={cn(primaryText, smallTextSize, semiBoldText, 'block mb-2')}>
              {t('profile.demoExtend.section')}
            </label>
            <select
              id="demo-extend-section"
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              disabled={isGenerating || !!result}
              className={cn(
                'w-full px-4 py-2 rounded-lg border bg-background dark:bg-background-dark',
                borderPrimary,
                primaryText,
                smallTextSize,
                'focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark disabled:opacity-50'
              )}
            >
              <option value="">{t('profile.demoExtend.selectSection')}</option>
              {sections.length > 1 && <option value={ALL_SECTIONS}>{t('profile.demoExtend.allSections')}</option>}
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isLoadingPreview && (
          <div className={cn(mutedText, smallTextSize, 'flex items-center gap-2')}>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('profile.demoExtend.loadingPreview')}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <p className={cn(smallTextSize, 'text-red-700 dark:text-red-400 break-words')}>{error}</p>
          </div>
        )}

        {preview && !preview.canExtend && (
          <Callout
            tone="warning"
            text={
              preview.reason === 'UP_TO_DATE'
                ? t('profile.demoExtend.reason.upToDate')
                : t('profile.demoExtend.reason.noCostPriceAnalyses')
            }
          />
        )}

        {preview?.canExtend && (
          <div className="space-y-3">
            {preview.sections.map(section => (
              <div key={section.sectionId} className="space-y-2">
                {/* A single section needs no header — the dropdown above already names it. */}
                {preview.sections.length > 1 && (
                  <p className={cn(mutedText, 'text-xs uppercase tracking-wide', semiBoldText)}>{section.sectionName}</p>
                )}
                {renderSectionGap(section)}
              </div>
            ))}
            <GapRow
              label={t('profile.demoExtend.inventory')}
              detail={t('profile.demoExtend.inventoryDetail')}
              amount={t('profile.demoExtend.monthsMissing', { count: preview.inventory.monthsMissing })}
            />
          </div>
        )}

        {result && (
          <Callout
            tone="success"
            text={
              result.sections.length > 1
                ? [
                    t('profile.demoExtend.successMultiSection', {
                      count: result.sections.length,
                      sessions: result.inventorySessionsCreated,
                    }),
                    ...result.sections.map(section =>
                      t('profile.demoExtend.successPerSection', {
                        section: section.sectionName,
                        revenueDays: section.revenueDaysGenerated,
                        orderDays: section.orderDaysGenerated,
                      })
                    ),
                  ].join(' ')
                : t('profile.demoExtend.success', {
                    revenueDays: result.revenueDaysGenerated,
                    orderDays: result.orderDaysGenerated,
                    sessions: result.inventorySessionsCreated,
                  })
            }
          />
        )}
        {!!result?.warnings?.length && (
          <details className={cn(primaryText, 'space-y-3 break-words')}>
            <summary className="cursor-pointer">
              {t('profile.demoExtend.reviewWarnings', { count: result.warnings.length })}
            </summary>
            {result.warnings.map((warning, index) => (
              <Callout key={index} tone="warning" text={warning} />
            ))}
          </details>
        )}
      </div>
    </InventorySlideOver>
  );
};

export default DemoExtendModal;
